import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import QRCode from "qrcode";
import { useNavigate, useParams } from "react-router-dom";
import { apiUrl } from "../../config/api";
import ConfirmDialog from "../globalcontrols/confirmdialog/ConfirmDialog";
import "./tableeAdmin.css";

function isLocalhostApp() {
  if (typeof window === "undefined") return false;
  return ["localhost", "127.0.0.1"].includes(window.location.hostname);
}

function getDefaultTableePublicBaseUrl() {
  if (isLocalhostApp()) return "http://127.0.0.1:5180";
  return process.env.REACT_APP_TABLEE_PUBLIC_BASE_URL || "https://tablee.cl";
}

const IS_LOCALHOST_APP = isLocalhostApp();
const TABLEE_PUBLIC_BASE_URL = getDefaultTableePublicBaseUrl();
const LABEL_WIDTH = 620;
const LABEL_HEIGHT = 424;
const LABEL_UNITS_PER_MM = 10;

function getSessionHeaders() {
  const session = JSON.parse(localStorage.getItem("sessionData") || "{}");
  return {
    Authorization: session.sessionKey || "",
    Username: session.username || "",
    "Content-Type": "application/json",
  };
}

function formatQuantity(value) {
  const number = Number(value || 0);
  return Number.isInteger(number) ? String(number) : number.toLocaleString("es-CL", { maximumFractionDigits: 2 });
}

function getItemKey(item) {
  return String(item.id || item.productId || item.sku || item.productName);
}

function escapeSvg(value) {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function wrapSvgText(value, maxLength, maxLines) {
  const words = String(value || "").split(/\s+/).filter(Boolean);
  const lines = [];
  let current = "";
  words.forEach((word) => {
    const next = current ? `${current} ${word}` : word;
    if (next.length <= maxLength) {
      current = next;
      return;
    }
    if (current) lines.push(current);
    current = word;
  });
  if (current) lines.push(current);
  return lines.slice(0, maxLines);
}

function createPackageKey() {
  const bytes = new Uint8Array(12);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("").toUpperCase();
}

function buildPackageDetailsUrl(order, labelPackage, publicBaseUrl = TABLEE_PUBLIC_BASE_URL) {
  const trackingNumber = encodeURIComponent(order.trackingNumber || "");
  const packageKey = encodeURIComponent(labelPackage.packageKey || labelPackage.key || "");
  return `${publicBaseUrl.replace(/\/+$/, "")}/#/paquete/${trackingNumber}/${packageKey}`;
}

function buildQrSvg(value, x, y, size) {
  const qr = QRCode.create(value, { errorCorrectionLevel: "M", margin: 0 });
  const moduleSize = size / qr.modules.size;
  const rects = [];
  for (let row = 0; row < qr.modules.size; row += 1) {
    for (let col = 0; col < qr.modules.size; col += 1) {
      if (qr.modules.get(row, col)) {
        rects.push(`<rect x="${(x + (col * moduleSize)).toFixed(2)}" y="${(y + (row * moduleSize)).toFixed(2)}" width="${moduleSize.toFixed(2)}" height="${moduleSize.toFixed(2)}"/>`);
      }
    }
  }
  return `<g class="qr"><rect x="${x - 8}" y="${y - 8}" width="${size + 16}" height="${size + 16}" rx="8" class="qr-bg"/>${rects.join("")}</g>`;
}

function getCustomerDisplayName(order) {
  const name = String(order?.customerName || "").trim();
  const id = String(order?.customerId || "").trim();
  if (!name) return "Cliente sin nombre";
  if (id && name === id) return "Cliente sin nombre";
  if (/^(cliente\s*)?#?\d+$/i.test(name)) return "Cliente sin nombre";
  return name;
}

function getReceiverName(order) {
  return getCustomerDisplayName(order);
}

function getDeliveryAddress(order) {
  return [
    order.deliveryAddress,
    order.deliveryCommune,
  ].filter(Boolean).join(", ") || "-";
}

function buildInitialPackage(order) {
  const items = {};
  (order.items || []).forEach((item) => {
    items[getItemKey(item)] = Number(item.quantity || 0);
  });
  return { id: `package-${Date.now()}`, packageKey: createPackageKey(), items };
}

function buildLabelSvg(order, labelPackage, packageIndex, totalPackages, logoDataUrl, publicBaseUrl) {
  const receiverLines = wrapSvgText(getReceiverName(order), 28, 2);
  const receiverSvg = receiverLines.map((line, index) => (
    `<text x="34" y="${196 + (index * 18)}" class="small">${escapeSvg(line)}</text>`
  )).join("");
  const addressLines = wrapSvgText(getDeliveryAddress(order), 46, 3);
  const addressSvg = addressLines.map((line, index) => (
    `<text x="34" y="${270 + (index * 18)}" class="small">${escapeSvg(line)}</text>`
  )).join("");
  const qrSvg = buildQrSvg(buildPackageDetailsUrl(order, labelPackage, publicBaseUrl), 478, 219, 92);
  const assignedItems = (order.items || [])
    .map((item) => ({ ...item, assignedQuantity: Number(labelPackage.items[getItemKey(item)] || 0) }))
    .filter((item) => item.assignedQuantity > 0);
  const assignedUnits = assignedItems.reduce((sum, item) => sum + Number(item.assignedQuantity || 0), 0);
  const contentSummary = assignedItems.length
    ? `${assignedItems.length} producto(s) / ${formatQuantity(assignedUnits)} unidad(es)`
    : "Sin productos asignados";
  const productDetailLines = assignedItems
    .flatMap((item) => wrapSvgText(`${formatQuantity(item.assignedQuantity)}x ${item.productName || item.sku || "Producto"}`, 60, 2));
  const detailStartY = 402;
  const detailLineHeight = 17;
  const labelHeight = Math.max(
    LABEL_HEIGHT,
    productDetailLines.length ? detailStartY + ((productDetailLines.length - 1) * detailLineHeight) + 30 : LABEL_HEIGHT,
  );
  const productDetailsSvg = productDetailLines.map((line, index) => (
    `<text x="34" y="${detailStartY + (index * detailLineHeight)}" class="content-detail">${escapeSvg(line)}</text>`
  )).join("");
  const logoSvg = logoDataUrl
    ? `<image x="34" y="24" width="104" height="44" href="${escapeSvg(logoDataUrl)}" preserveAspectRatio="xMidYMid meet" filter="url(#mono)"/>`
    : `<text x="34" y="58" class="brand-text">TABLEE</text>`;

  return `<svg xmlns="http://www.w3.org/2000/svg" width="62mm" height="${(labelHeight / LABEL_UNITS_PER_MM).toFixed(1)}mm" viewBox="0 0 ${LABEL_WIDTH} ${labelHeight}">
    <style>
      .bg{fill:#fff}.border{fill:none;stroke:#000;stroke-width:4}.rule{stroke:#000;stroke-width:1.6;opacity:.35}
      .eyebrow{fill:#000;font-family:Arial,sans-serif;font-size:14px;font-weight:800;letter-spacing:1px}
      .brand-text{fill:#000;font-family:Arial,sans-serif;font-size:30px;font-weight:900;letter-spacing:1px}
      .title{fill:#000;font-family:Arial,sans-serif;font-size:25px;font-weight:900}
      .label{fill:#000;font-family:Arial,sans-serif;font-size:12px;font-weight:900}
      .value{fill:#000;font-family:Arial,sans-serif;font-size:18px;font-weight:900}
      .small{fill:#000;font-family:Arial,sans-serif;font-size:14px;font-weight:800}
      .item{fill:#000;font-family:Arial,sans-serif;font-size:14px;font-weight:900}
      .content-detail{fill:#000;font-family:Arial,sans-serif;font-size:13px;font-weight:850}
      .qr-bg{fill:#fff;stroke:#000;stroke-width:2;opacity:1}
    </style>
    <defs>
      <filter id="mono"><feColorMatrix type="saturate" values="0"/></filter>
    </defs>
    <rect class="bg" x="0" y="0" width="${LABEL_WIDTH}" height="${labelHeight}"/>
    <rect class="border" x="12" y="12" width="596" height="${labelHeight - 24}" rx="8"/>
    ${logoSvg}
    <text x="160" y="42" class="eyebrow">DESPACHO TABLEE</text>
    <text x="160" y="72" class="title">Paquete ${packageIndex + 1} de ${totalPackages}</text>
    <line x1="34" y1="92" x2="586" y2="92" class="rule"/>
    <text x="34" y="122" class="label">ORDEN</text>
    <text x="34" y="148" class="value">${escapeSvg(order.publicId || "-")}</text>
    <text x="284" y="122" class="label">SEGUIMIENTO</text>
    <text x="284" y="148" class="value">${escapeSvg(order.trackingNumber || "-")}</text>
    <text x="34" y="176" class="label">RECEPTOR</text>
    ${receiverSvg}
    <text x="284" y="176" class="label">TELEFONO</text>
    <text x="284" y="198" class="small">${escapeSvg(order.customerPhone || "-")}</text>
    <text x="34" y="250" class="label">DIRECCION DE ENTREGA</text>
    ${addressSvg}
    <text x="478" y="192" class="label">DETALLES</text>
    ${qrSvg}
    <line x1="34" y1="354" x2="586" y2="354" class="rule"/>
    <text x="34" y="378" class="label">CONTENIDO</text>
    <text x="130" y="378" class="item">${escapeSvg(contentSummary)}</text>
    ${productDetailsSvg}
  </svg>`;
}

function downloadBlob(fileName, blob) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function getSvgDimensions(svg) {
  const match = String(svg || "").match(/viewBox="0 0\s+([\d.]+)\s+([\d.]+)"/);
  return {
    width: match ? Number(match[1]) : LABEL_WIDTH,
    height: match ? Number(match[2]) : LABEL_HEIGHT,
  };
}

function svgToPngBlob(svg, scale = 4) {
  return new Promise((resolve, reject) => {
    const dimensions = getSvgDimensions(svg);
    const svgBlob = new Blob([svg], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(svgBlob);
    const image = new Image();
    image.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = dimensions.width * scale;
      canvas.height = dimensions.height * scale;
      const context = canvas.getContext("2d");
      context.fillStyle = "#fff";
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error("No se pudo generar la etiqueta PNG."));
      }, "image/png");
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("No se pudo preparar la etiqueta PNG."));
    };
    image.src = url;
  });
}

function LabelPreview({ order, labelPackage, packageIndex, totalPackages, logoDataUrl, publicBaseUrl }) {
  const [previewSrc, setPreviewSrc] = useState("");
  const svg = buildLabelSvg(order, labelPackage, packageIndex, totalPackages, logoDataUrl, publicBaseUrl);

  useEffect(() => {
    let ignore = false;
    async function renderPreview() {
      try {
        const pngBlob = await svgToPngBlob(svg, 3);
        const dataUrl = await blobToDataUrl(pngBlob);
        if (!ignore) setPreviewSrc(dataUrl);
      } catch (error) {
        if (!ignore) setPreviewSrc("");
      }
    }
    renderPreview();
    return () => {
      ignore = true;
    };
  }, [svg]);

  return (
    <div className="tablee-label-designer__preview" aria-label={`Etiqueta paquete ${packageIndex + 1}`}>
      {previewSrc ? <img src={previewSrc} alt={`Etiqueta paquete ${packageIndex + 1}`} /> : <span>Generando PNG...</span>}
    </div>
  );
}

function ProductDropdown({ value, options, placeholder, disabled, isOpen, onOpen, onClose, onChange }) {
  const triggerRef = useRef(null);
  const [menuRect, setMenuRect] = useState(null);
  const selectedOption = options.find((option) => String(option.value) === String(value));

  useEffect(() => {
    if (!isOpen || !triggerRef.current) return undefined;

    function updateMenuRect() {
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuRect({
        left: rect.left,
        top: rect.bottom + 6,
        width: rect.width,
      });
    }

    updateMenuRect();
    window.addEventListener("resize", updateMenuRect);
    window.addEventListener("scroll", updateMenuRect, true);
    return () => {
      window.removeEventListener("resize", updateMenuRect);
      window.removeEventListener("scroll", updateMenuRect, true);
    };
  }, [isOpen]);

  const menu = isOpen && menuRect ? (
    <div
      className="tablee-label-designer__product-menu"
      role="listbox"
      style={{ left: menuRect.left, top: menuRect.top, width: menuRect.width }}
    >
      {options.length ? options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="option"
          aria-selected={String(option.value) === String(value)}
          className={String(option.value) === String(value) ? "is-selected" : ""}
          onClick={() => {
            onChange(option.value);
            onClose();
          }}
        >
          {option.label}
        </button>
      )) : (
        <span className="tablee-label-designer__product-menu-empty">Sin productos disponibles</span>
      )}
    </div>
  ) : null;

  return (
    <div className={`tablee-label-designer__product-select ${disabled ? "is-disabled" : ""}`}>
      <button
        ref={triggerRef}
        type="button"
        className="tablee-label-designer__product-select-trigger"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => (isOpen ? onClose() : onOpen())}
      >
        <span className="material-symbols-outlined">inventory_2</span>
        <span className={selectedOption ? "" : "is-placeholder"}>{selectedOption?.label || placeholder}</span>
        <span className="material-symbols-outlined">expand_more</span>
      </button>
      {menu ? createPortal(menu, document.body) : null}
    </div>
  );
}

function TableeShippingLabels({ apiPrefix = "/tablee", storeName = "Tablee", storeLogo = "/images/tablee_logo.png", publicBaseUrl, ordersPath = "/app/tablee-ordenes" } = {}) {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [packages, setPackages] = useState([]);
  const [message, setMessage] = useState("");
  const [editingPackageDraft, setEditingPackageDraft] = useState(null);
  const [previewPackageId, setPreviewPackageId] = useState("");
  const [logoDataUrl, setLogoDataUrl] = useState("");
  const [productToAddKey, setProductToAddKey] = useState("");
  const [isProductDropdownOpen, setIsProductDropdownOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null);

  useEffect(() => {
    let ignore = false;
    async function loadLogo() {
      try {
        const response = await fetch(storeLogo);
        const blob = await response.blob();
        const dataUrl = await blobToDataUrl(blob);
        if (!ignore) setLogoDataUrl(dataUrl);
      } catch (error) {
        if (!ignore) setLogoDataUrl("");
      }
    }
    loadLogo();
    return () => {
      ignore = true;
    };
  }, [storeLogo]);

  useEffect(() => {
    let ignore = false;
    async function loadOrder() {
      setMessage("");
      try {
        const response = await fetch(apiUrl(`${apiPrefix}/orders`), { headers: getSessionHeaders() });
        const data = await response.json();
        if (!data.success) {
          setMessage(data.message || "No se pudo cargar la orden.");
          return;
        }
        const selectedOrder = (data.orders || []).find((entry) => String(entry.id) === String(orderId));
        if (!selectedOrder) {
          setMessage("Orden no encontrada.");
          return;
        }
        const packagesResponse = await fetch(apiUrl(`${apiPrefix}/orders/${selectedOrder.id}/packages`), { headers: getSessionHeaders() });
        const packagesData = await packagesResponse.json();
        const savedPackages = packagesData.success ? (packagesData.packages || []) : [];
        if (!ignore) {
          const initialPackage = savedPackages.length
            ? savedPackages.map((entry, index) => ({
              id: `package-${entry.packageKey || entry.key || index}`,
              packageKey: entry.packageKey || entry.key || createPackageKey(),
              items: entry.items || {},
            }))
            : [buildInitialPackage(selectedOrder)];
          setOrder(selectedOrder);
          setPackages(initialPackage);
          setEditingPackageDraft(null);
          setPreviewPackageId("");
        }
      } catch (error) {
        if (!ignore) setMessage(`No se pudo conectar con ${storeName}.`);
      }
    }
    loadOrder();
    return () => {
      ignore = true;
    };
  }, [orderId, apiPrefix, storeName]);

  function addPackage() {
    setEditingPackageDraft({
      id: `package-${Date.now()}-${packages.length}`,
      packageKey: createPackageKey(),
      items: {},
      isNew: true,
    });
    setProductToAddKey("");
    setIsProductDropdownOpen(false);
  }

  function openPackageEditor(packageId) {
    const selectedPackage = packages.find((entry) => entry.id === packageId);
    if (!selectedPackage) return;
    setEditingPackageDraft({
      ...selectedPackage,
      items: { ...(selectedPackage.items || {}) },
      isNew: false,
    });
    setProductToAddKey("");
    setIsProductDropdownOpen(false);
  }

  function removePackage(packageId) {
    setIsProductDropdownOpen(false);
    setPackages((current) => {
      if (current.length <= 1) return current;
      const next = current.filter((labelPackage) => labelPackage.id !== packageId);
      if (editingPackageDraft?.id === packageId) setEditingPackageDraft(null);
      if (previewPackageId === packageId) setPreviewPackageId("");
      return next;
    });
  }

  function confirmRemovePackage(packageId, packageIndex) {
    setConfirmDialog({
      title: "Eliminar paquete",
      message: `Eliminar el paquete ${packageIndex + 1} de esta etiqueta?`,
      confirmLabel: "Eliminar",
      onConfirm: () => {
        removePackage(packageId);
        setConfirmDialog(null);
      },
    });
  }

  function updateDraftPackageItem(item, value) {
    const itemKey = getItemKey(item);
    setEditingPackageDraft((currentDraft) => {
      if (!currentDraft) return currentDraft;
      const assignedInOtherPackages = packages.reduce((sum, entry) => {
        if (entry.id === currentDraft.id) return sum;
        return sum + Number(entry.items?.[itemKey] || 0);
      }, 0);
      const maxAllowed = Math.max(0, Number(item.quantity || 0) - assignedInOtherPackages);
      const parsed = Math.max(0, Math.min(maxAllowed, Number(value || 0)));
      return {
        ...currentDraft,
        items: {
          ...currentDraft.items,
          [itemKey]: parsed,
        },
      };
    });
  }

  function addProductToPackage(item) {
    updateDraftPackageItem(item, 1);
    setProductToAddKey("");
    setIsProductDropdownOpen(false);
  }

  function removeProductFromPackage(item) {
    updateDraftPackageItem(item, 0);
  }

  function confirmRemoveProductFromPackage(item) {
    setConfirmDialog({
      title: "Quitar producto",
      message: `Quitar "${item.productName || "este producto"}" del paquete?`,
      confirmLabel: "Quitar",
      onConfirm: () => {
        removeProductFromPackage(item);
        setConfirmDialog(null);
      },
    });
  }

  function closePackageEditor() {
    setEditingPackageDraft(null);
    setProductToAddKey("");
    setIsProductDropdownOpen(false);
  }

  function savePackageDraft() {
    if (!editingPackageDraft) return;
    const packageToSave = {
      id: editingPackageDraft.id,
      packageKey: editingPackageDraft.packageKey || createPackageKey(),
      items: editingPackageDraft.items || {},
    };
    setPackages((current) => (
      editingPackageDraft.isNew
        ? [...current, packageToSave]
        : current.map((entry) => (entry.id === packageToSave.id ? packageToSave : entry))
    ));
    closePackageEditor();
  }

  async function downloadLabel(labelPackage, packageIndex) {
    if (!order) return;
    try {
      const savedPackages = await savePackages();
      const savedPackage = savedPackages[packageIndex] || labelPackage;
      const svg = buildLabelSvg(order, savedPackage, packageIndex, savedPackages.length, logoDataUrl, publicBaseUrl);
      const pngBlob = await svgToPngBlob(svg);
      downloadBlob(`${order.publicId}-paquete-${packageIndex + 1}-de-${savedPackages.length}.png`, pngBlob);
    } catch (error) {
      setMessage(error.message || "No se pudo descargar la etiqueta.");
    }
  }

  async function downloadAllLabels() {
    if (!order) return;
    try {
      const savedPackages = await savePackages();
      const labelImages = await Promise.all(savedPackages.map(async (labelPackage, index) => {
        const svg = buildLabelSvg(order, labelPackage, index, savedPackages.length, logoDataUrl, publicBaseUrl);
        const pngBlob = await svgToPngBlob(svg);
        return blobToDataUrl(pngBlob);
      }));
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Etiquetas ${order.publicId}</title><style>
      @page{size:auto;margin:0}
      body{margin:0;padding:0;background:#fff;font-family:Arial,sans-serif}
      .label{width:62mm;page-break-after:always;break-after:page}
      .label img{width:62mm;height:auto;display:block}
      @media screen{body{padding:12px;background:#eee}.label{margin:0 auto 12px;background:#fff}}
    </style></head><body>${labelImages.map((src) => `<div class="label"><img src="${src}" alt="Etiqueta de despacho"></div>`).join("")}</body></html>`;
      downloadBlob(`${order.publicId}-etiquetas.html`, new Blob([html], { type: "text/html;charset=utf-8" }));
    } catch (error) {
      setMessage(error.message || "No se pudieron descargar las etiquetas.");
    }
  }

  async function savePackages() {
    if (!order) return packages;
    const payloadPackages = packages.map((labelPackage) => ({
      packageKey: labelPackage.packageKey || labelPackage.key || createPackageKey(),
      items: labelPackage.items || {},
    }));
    const response = await fetch(apiUrl(`${apiPrefix}/orders/${order.id}/packages`), {
      method: "PUT",
      headers: getSessionHeaders(),
      body: JSON.stringify({ packages: payloadPackages }),
    });
    const data = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(data.message || "No se pudieron guardar los paquetes.");
    }
    const savedPackages = (data.packages || []).map((entry, index) => ({
      id: packages[index]?.id || `package-${entry.packageKey || entry.key || index}`,
      packageKey: entry.packageKey || entry.key || payloadPackages[index]?.packageKey || createPackageKey(),
      items: entry.items || payloadPackages[index]?.items || {},
    }));
    setPackages(savedPackages);
    setMessage("Paquetes guardados para codigo QR.");
    return savedPackages;
  }

  async function previewSavedPackage(packageIndex) {
    try {
      const savedPackages = await savePackages();
      const savedPackage = savedPackages[packageIndex];
      if (savedPackage) setPreviewPackageId(savedPackage.id);
    } catch (error) {
      setMessage(error.message || "No se pudo preparar el codigo QR.");
    }
  }

  async function openPackageQrView(packageIndex) {
    try {
      const savedPackages = await savePackages();
      const savedPackage = savedPackages[packageIndex];
      if (!savedPackage) return;
      window.open(buildPackageDetailsUrl(order, savedPackage), "_blank", "noopener,noreferrer");
    } catch (error) {
      setMessage(error.message || "No se pudo abrir la vista QR local.");
    }
  }

  function getPackageSummary(labelPackage) {
    const assignedItems = (order.items || [])
      .map((item) => Number(labelPackage.items[getItemKey(item)] || 0))
      .filter((quantity) => quantity > 0);
    const units = assignedItems.reduce((sum, quantity) => sum + quantity, 0);
    return {
      productCount: assignedItems.length,
      units,
    };
  }

  const editingPackage = editingPackageDraft;
  const editingPackageIndex = editingPackage
    ? editingPackage.isNew
      ? packages.length
      : packages.findIndex((entry) => entry.id === editingPackage.id)
    : -1;
  const editingPackageTotal = editingPackage?.isNew ? packages.length + 1 : packages.length;
  const previewPackage = packages.find((entry) => entry.id === previewPackageId);
  const previewPackageIndex = previewPackage ? packages.findIndex((entry) => entry.id === previewPackage.id) : -1;
  const editingPackageItems = editingPackage
    ? (order?.items || []).filter((item) => Number(editingPackage.items[getItemKey(item)] || 0) > 0)
    : [];
  const addableItems = useMemo(() => (
    editingPackage
      ? (order?.items || []).filter((item) => {
      const itemKey = getItemKey(item);
      const current = Number(editingPackage.items[itemKey] || 0);
      const assigned = packages.reduce((sum, entry) => {
        if (entry.id === editingPackage.id) return sum;
        return sum + Number(entry.items?.[itemKey] || 0);
      }, 0);
      return current <= 0 && Number(item.quantity || 0) - assigned > 0;
    })
      : []
  ), [editingPackage, order?.items, packages]);
  const addableProductOptions = addableItems.map((item) => ({
    value: getItemKey(item),
    label: item.productName || item.sku || "Producto",
  }));
  const selectedProductToAdd = addableItems.find((item) => getItemKey(item) === productToAddKey) || addableItems[0];

  useEffect(() => {
    if (!editingPackage || !addableItems.length) {
      if (productToAddKey) setProductToAddKey("");
      return;
    }

    const hasSelectedProduct = addableItems.some((item) => String(getItemKey(item)) === String(productToAddKey));
    if (!hasSelectedProduct) {
      setProductToAddKey(getItemKey(addableItems[0]));
    }
  }, [editingPackage, addableItems, productToAddKey]);

  if (!order) {
    return (
      <section className="tablee-admin tablee-label-designer">
        <header className="tablee-admin__header">
          <img src={storeLogo} alt={storeName} />
          <div>
            <span>{storeName}</span>
            <h1>Etiquetas de despacho</h1>
            <p>{message || "Cargando orden..."}</p>
          </div>
        </header>
      </section>
    );
  }

  return (
    <section className="tablee-admin tablee-label-designer">
      <button type="button" className="tablee-label-designer__top-back" onClick={() => navigate(ordersPath)}>
        <span className="material-symbols-outlined">arrow_back</span>
        Ordenes
      </button>
      <header className="tablee-admin__header">
        <img src={storeLogo} alt={storeName} />
        <div>
          <span>Etiquetas de despacho</span>
          <h1>{order.publicId}</h1>
          <p>{order.trackingNumber || "Sin numero de seguimiento"} · {packages.length} paquete(s)</p>
        </div>
      </header>

      <div className="tablee-label-designer__stack">
        <section className="tablee-label-designer__table-panel">
          <div className="tablee-label-designer__toolbar">
            <div>
              <span>Separacion de carga</span>
              <h2>Paquetes</h2>
            </div>
            <div className="tablee-label-designer__toolbar-actions">
              <button type="button" onClick={addPackage}>
                <span className="material-symbols-outlined">add</span>
                Agregar
              </button>
              <button type="button" className="tablee-label-designer__download-all" onClick={downloadAllLabels} aria-label="Descargar todas las etiquetas">
                <span className="material-symbols-outlined">download</span>
                <span className="tablee-label-designer__download-all-text">Descargar todas</span>
              </button>
            </div>
          </div>
          <div className="tablee-label-designer__table">
            <div className="tablee-label-designer__table-head">
              <span>Paquete</span>
              <span>Productos</span>
              <span>Unidades</span>
              <span>Estado</span>
              <span>Acciones</span>
            </div>
            {packages.map((labelPackage, index) => {
              const summary = getPackageSummary(labelPackage);
              return (
                <div className="tablee-label-designer__table-row" key={labelPackage.id}>
                  <span className="tablee-label-designer__package-name" data-label="Paquete">
                    Paquete {index + 1} / {packages.length}
                  </span>
                  <span className="tablee-label-designer__package-products" data-label="Productos">{summary.productCount} producto(s)</span>
                  <span className="tablee-label-designer__package-units" data-label="Unidades">{formatQuantity(summary.units)} unidad(es)</span>
                  <span data-label="Estado">{summary.productCount ? "Con carga" : "Sin carga"}</span>
                  <div className="tablee-label-designer__row-actions" data-label="Acciones">
                    <button type="button" onClick={() => openPackageEditor(labelPackage.id)} title="Editar contenido" aria-label={`Editar contenido paquete ${index + 1}`}>
                      <span className="material-symbols-outlined">edit</span>
                    </button>
                    <button type="button" onClick={() => previewSavedPackage(index)} title="Ver etiqueta" aria-label={`Ver etiqueta paquete ${index + 1}`}>
                      <span className="material-symbols-outlined">visibility</span>
                    </button>
                    {IS_LOCALHOST_APP ? (
                      <button type="button" onClick={() => openPackageQrView(index)} title="Ver QR local" aria-label={`Ver vista QR local paquete ${index + 1}`}>
                        <span className="material-symbols-outlined">qr_code_scanner</span>
                      </button>
                    ) : null}
                    <button type="button" onClick={() => downloadLabel(labelPackage, index)} title="Descargar etiqueta" aria-label={`Descargar etiqueta paquete ${index + 1}`}>
                      <span className="material-symbols-outlined">download</span>
                    </button>
                    <button type="button" onClick={() => confirmRemovePackage(labelPackage.id, index)} disabled={packages.length <= 1} title="Eliminar paquete" aria-label={`Eliminar paquete ${index + 1}`}>
                      <span className="material-symbols-outlined">delete</span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

      </div>

      {editingPackage && (
        <div className="tablee-label-designer__modal-backdrop" role="presentation" onClick={closePackageEditor}>
          <section className="tablee-label-designer__modal tablee-label-designer__modal--edit" role="dialog" aria-modal="true" aria-label={`Editar paquete ${editingPackageIndex + 1}`} onClick={(event) => event.stopPropagation()}>
            <header className="tablee-label-designer__modal-head">
              <div>
                <span>Asignacion de productos</span>
                <h2>Paquete {editingPackageIndex + 1} de {editingPackageTotal}</h2>
              </div>
              <button type="button" onClick={closePackageEditor} aria-label="Cerrar editor">
                <span className="material-symbols-outlined">close</span>
              </button>
            </header>
            <div className="tablee-label-designer__modal-body">
              <div className="tablee-label-designer__add-product">
                <ProductDropdown
                  value={selectedProductToAdd ? getItemKey(selectedProductToAdd) : ""}
                  options={addableProductOptions}
                  placeholder="Sin productos disponibles"
                  disabled={!addableItems.length}
                  isOpen={isProductDropdownOpen}
                  onOpen={() => setIsProductDropdownOpen(true)}
                  onClose={() => setIsProductDropdownOpen(false)}
                  onChange={setProductToAddKey}
                />
                <button type="button" onClick={() => selectedProductToAdd && addProductToPackage(selectedProductToAdd)} disabled={!selectedProductToAdd}>
                  <span className="material-symbols-outlined">add</span>
                  Agregar producto
                </button>
              </div>

              <div className="tablee-label-designer__assignment-list">
                {editingPackageItems.length ? editingPackageItems.map((item) => {
                  const itemKey = getItemKey(item);
                  const assigned = packages.reduce((sum, entry) => {
                    if (entry.id === editingPackage.id) return sum;
                    return sum + Number(entry.items?.[itemKey] || 0);
                  }, 0);
                  const current = Number(editingPackage.items[itemKey] || 0);
                  const remaining = Number(item.quantity || 0) - assigned;
                  const availableForThisPackage = Math.max(0, remaining + current);
                  return (
                    <label className="tablee-label-designer__assignment" key={itemKey}>
                      <span>
                        <strong>{formatQuantity(current)}x {item.productName}</strong>
                        <small>Pedido: {formatQuantity(item.quantity)} · Disponible: {formatQuantity(availableForThisPackage)}</small>
                      </span>
                      <div className="tablee-label-designer__quantity-stepper">
                        <button type="button" onClick={() => updateDraftPackageItem(item, current - 1)} disabled={current <= 0} aria-label={`Bajar cantidad de ${item.productName}`}>
                          <span className="material-symbols-outlined">remove</span>
                        </button>
                        <input
                          type="number"
                          inputMode="numeric"
                          min="0"
                          max={availableForThisPackage}
                          step="1"
                          value={current}
                          onChange={(event) => updateDraftPackageItem(item, event.target.value)}
                          aria-label={`Cantidad de ${item.productName}`}
                        />
                        <button type="button" onClick={() => updateDraftPackageItem(item, current + 1)} disabled={current >= availableForThisPackage} aria-label={`Subir cantidad de ${item.productName}`}>
                          <span className="material-symbols-outlined">add</span>
                        </button>
                      </div>
                      <button type="button" onClick={() => confirmRemoveProductFromPackage(item)} aria-label={`Quitar ${item.productName}`}>
                        <span className="material-symbols-outlined">delete</span>
                      </button>
                    </label>
                  );
                }) : (
                  <div className="tablee-label-designer__empty-package">
                    <span className="material-symbols-outlined">inventory_2</span>
                    <strong>Este paquete no tiene productos</strong>
                    <p>Agrega productos disponibles desde la orden para construir esta etiqueta.</p>
                  </div>
                )}
              </div>
            </div>
            <footer className="tablee-label-designer__modal-actions">
              <button type="button" className="tablee-label-designer__secondary-action" onClick={closePackageEditor}>Cancelar</button>
              <button type="button" onClick={savePackageDraft}>Guardar</button>
            </footer>
          </section>
        </div>
      )}

      {previewPackage && (
        <div className="tablee-label-designer__modal-backdrop" role="presentation" onClick={() => setPreviewPackageId("")}>
          <section className="tablee-label-designer__modal tablee-label-designer__modal--preview" role="dialog" aria-modal="true" aria-label={`Vista previa paquete ${previewPackageIndex + 1}`} onClick={(event) => event.stopPropagation()}>
            <header className="tablee-label-designer__modal-head">
              <div>
                <span>Etiqueta printable</span>
                <h2>Paquete {previewPackageIndex + 1} de {packages.length}</h2>
              </div>
              <button type="button" onClick={() => setPreviewPackageId("")} aria-label="Cerrar vista previa">
                <span className="material-symbols-outlined">close</span>
              </button>
            </header>
            <div className="tablee-label-designer__preview-stage">
              <LabelPreview order={order} labelPackage={previewPackage} packageIndex={previewPackageIndex} totalPackages={packages.length} logoDataUrl={logoDataUrl} publicBaseUrl={publicBaseUrl} />
            </div>
          </section>
        </div>
      )}

      <ConfirmDialog
        open={Boolean(confirmDialog)}
        title={confirmDialog?.title}
        message={confirmDialog?.message}
        confirmLabel={confirmDialog?.confirmLabel}
        onConfirm={confirmDialog?.onConfirm}
        onCancel={() => setConfirmDialog(null)}
      />
    </section>
  );
}

export default TableeShippingLabels;
